import { useState, useEffect, useCallback } from "react"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email)
}

export interface UseConfirmableEmailProps {
  value: string
  onConfirm: (email: string) => void
  error?: string
}

export interface UseConfirmableEmailReturn {
  isEditing: boolean
  draftValue: string
  draftIsValid: boolean
  canCancel: boolean
  displayError: string | undefined

  startEditing: () => void
  confirm: () => void
  cancel: () => void
  changeDraft: (newValue: string) => void
}

export function useConfirmableEmail({
  value,
  onConfirm,
  error,
}: UseConfirmableEmailProps): UseConfirmableEmailReturn {
  const isConfirmed = value !== "" && isValidEmail(value)
  const [isEditing, setIsEditing] = useState(!isConfirmed)
  const [draftValue, setDraftValue] = useState(value)

  useEffect(() => {
    if (value !== "" && isValidEmail(value)) {
      setDraftValue(value)
      setIsEditing(false)
    }
  }, [value])

  const canCancel = value !== "" && isValidEmail(value)
  const draftIsValid = isValidEmail(draftValue.trim())

  const formatError = draftValue.trim() !== "" && !draftIsValid
    ? "Enter a valid email address"
    : undefined

  const displayError = formatError ?? error

  const startEditing = useCallback(() => {
    setDraftValue(value)
    setIsEditing(true)
  }, [value])

  const confirm = useCallback(() => {
    const trimmed = draftValue.trim()
    if (!isValidEmail(trimmed)) return
    onConfirm(trimmed)
    setIsEditing(false)
  }, [draftValue, onConfirm])

  const cancel = useCallback(() => {
    if (!(value !== "" && isValidEmail(value))) return
    setDraftValue(value)
    setIsEditing(false)
  }, [value])

  const changeDraft = useCallback((newValue: string) => {
    setDraftValue(newValue)
  }, [])

  return {
    isEditing,
    draftValue,
    draftIsValid,
    canCancel,
    displayError,
    startEditing,
    confirm,
    cancel,
    changeDraft,
  }
}
